// src/services/mockApiService.js
// Lightweight in-memory + sessionStorage backed mock API that simulates backend endpoints.
// It is invoked by apiService when NO_SERVER mode is enabled.

import { mapPreferenceToGroupApiFormat, mapPreferenceToTeacherApiFormat, mapPreferenceFromApiFormat } from '../utils/preferenceMappers';
import logger from '../utils/logger';

const STORAGE_KEY = 'piSchedule.mockData';
const ID_TRACKER_KEY = 'piSchedule.mockData.idTracker';

let inMemoryStore = null;
let idTracker = null;

const ensureInitialized = async () => {
    if (inMemoryStore) return inMemoryStore;

    try {
        const cached = sessionStorage.getItem(STORAGE_KEY);
        const tracker = sessionStorage.getItem(ID_TRACKER_KEY);
        if (cached) {
            inMemoryStore = JSON.parse(cached);
            idTracker = tracker ? JSON.parse(tracker) : {};
            return inMemoryStore;
        }
    } catch (e) {
        // ignore
    }

    const resp = await fetch('/mock-data.json');
    if (!resp.ok) {
        throw new Error(`Failed to load mock-data.json: ${resp.status}`);
    }
    inMemoryStore = await resp.json();
    // Initialize tracker with max existing ids per collection
    idTracker = {};
    const collections = ['teachers', 'groups', 'students', 'rooms', 'subjects', 'schedule_entries', 'invitations', 'admins', 'drafts'];
    collections.forEach(col => {
        const arr = Array.isArray(inMemoryStore[col]) ? inMemoryStore[col] : [];
        idTracker[col] = arr.reduce((max, item) => item && item.id && item.id > max ? item.id : max, 0);
    });
    persist();
    return inMemoryStore;
};

const persist = () => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(inMemoryStore));
        sessionStorage.setItem(ID_TRACKER_KEY, JSON.stringify(idTracker));
    } catch (e) {
        // ignore
    }
};

const nextId = (collectionName) => {
    if (!idTracker[collectionName]) idTracker[collectionName] = 0;
    idTracker[collectionName] += 1;
    persist();
    return idTracker[collectionName];
};

const jsonResponse = (data) => data;

const parsePath = (path) => {
    const clean = path.startsWith('/') ? path.slice(1) : path;
    const parts = clean.split('?')[0].split('/');
    return parts;
};

// Helpers
const getById = (arr, id) => arr.find(i => String(i.id) === String(id));
const removeById = (arr, id) => {
    const idx = arr.findIndex(i => String(i.id) === String(id));
    if (idx >= 0) arr.splice(idx, 1);
};

// Helper to resolve qualifiedSubjects from IDs to full objects
const resolveQualifiedSubjects = (subjectIds) => {
    if (!Array.isArray(subjectIds)) return [];
    const subjects = inMemoryStore.subjects || [];
    return subjectIds
        .map(id => {
            const subject = getById(subjects, id);
            if (!subject) return null;
            return {
                id: subject.id,
                subject_code: subject.subject_code,
                name: subject.name,
                color: subject.color
            };
        })
        .filter(s => s !== null); // Filter out any missing subjects
};

// Entry point used by apiService
export const mockFetch = async (endpointPath, method = 'GET', body = null, queryParams = null /* options unused */) => {
    await ensureInitialized();

    const m = method.toUpperCase();
    const parts = parsePath(endpointPath);

    // Teachers
    if (parts[0] === 'teachers') {
        if (m === 'GET' && parts.length === 1) {
            // Resolve qualifiedSubjects for all teachers
            const teachers = (inMemoryStore.teachers || []).map(teacher => ({
                ...teacher,
                qualifiedSubjects: resolveQualifiedSubjects(teacher.qualifiedSubjects)
            }));
            return jsonResponse(teachers);
        }
        if (m === 'POST' && parts.length === 1) {
            const { teacher_code, name, email, tags } = body || {};
            const item = {
                id: nextId('teachers'),
                teacher_code,
                name,
                email,
                tags: Array.isArray(tags) ? tags : [],
                preferences: [],
                qualifiedSubjects: []
            };
            inMemoryStore.teachers.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const teacherId = parts[1];
            const teacher = getById(inMemoryStore.teachers, teacherId);
            if (!teacher) throw new Error('Teacher not found');
            if (m === 'GET' && parts.length === 2) {
                // Resolve qualifiedSubjects from IDs to full objects
                const resolvedTeacher = {
                    ...teacher,
                    qualifiedSubjects: resolveQualifiedSubjects(teacher.qualifiedSubjects)
                };
                return jsonResponse(resolvedTeacher);
            }
            if (m === 'PUT' && parts.length === 2) {
                Object.assign(teacher, body || {});
                if (body && body.hasOwnProperty('tags')) {
                    teacher.tags = Array.isArray(body.tags) ? body.tags : [];
                }
                persist();
                // Resolve qualifiedSubjects from IDs to full objects when returning
                const resolvedTeacher = {
                    ...teacher,
                    qualifiedSubjects: resolveQualifiedSubjects(teacher.qualifiedSubjects)
                };
                return jsonResponse(resolvedTeacher);
            }
            if (m === 'DELETE' && parts.length === 2) {
                removeById(inMemoryStore.teachers, teacherId);
                persist();
                return null;
            }
            if (parts[2] === 'preferences' && m === 'PUT') {
                const formatted = (Array.isArray(body) ? body : []).map(p => p.startTime ? mapPreferenceToTeacherApiFormat(p) : p);
                teacher.preferences = formatted;
                persist();
                return jsonResponse(formatted);
            }
            if (parts[2] === 'qualifications' && m === 'PUT') {
                // Extract IDs if full objects are passed, otherwise use IDs directly
                const subjectIds = Array.isArray(body) 
                    ? body.map(item => typeof item === 'object' && item !== null ? item.id : item)
                    : [];
                teacher.qualifiedSubjects = subjectIds;
                persist();
                // Return resolved full objects (matching backend behavior)
                return jsonResponse(resolveQualifiedSubjects(teacher.qualifiedSubjects));
            }
        }
    }

    // Groups
    if (parts[0] === 'groups') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.groups || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const { group_code, name } = body || {};
            const item = { id: nextId('groups'), group_code, name, students: [], preferences: [], tags: [] };
            inMemoryStore.groups.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const groupId = parts[1];
            const group = getById(inMemoryStore.groups, groupId);
            if (!group) throw new Error('Group not found');
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(group);
            }
            if (m === 'PUT' && parts.length === 2) {
                Object.assign(group, body || {});
                persist();
                return jsonResponse(group);
            }
            if (m === 'DELETE' && parts.length === 2) {
                removeById(inMemoryStore.groups, groupId);
                persist();
                return null;
            }
            if (parts[2] === 'students') {
                if (m === 'POST' && parts.length === 3) {
                    const studentId = body?.studentId;
                    const student = getById(inMemoryStore.students, studentId);
                    if (!student) throw new Error('Student not found');
                    if (!group.students.find(s => s.id === student.id)) {
                        group.students.push({ id: student.id, first_name: student.first_name, family_name: student.family_name, email: student.email });
                    }
                    persist();
                    return jsonResponse({ ok: true });
                }
                if (m === 'POST' && parts.length === 4 && parts[3] === 'batch') {
                    const students = Array.isArray(body?.students) ? body.students : [];
                    let successful = 0; let failed = 0;
                    students.forEach(s => {
                        try {
                            let existing = inMemoryStore.students.find(st => st.email === s.email);
                            if (!existing) {
                                existing = { id: nextId('students'), ...s };
                                inMemoryStore.students.push(existing);
                            }
                            if (!group.students.find(gs => gs.id === existing.id)) {
                                group.students.push({ id: existing.id, first_name: existing.first_name, family_name: existing.family_name, email: existing.email });
                            }
                            successful += 1;
                        } catch (e) { failed += 1; }
                    });
                    persist();
                    return jsonResponse({ results: { successful, failed } });
                }
                if (m === 'DELETE' && parts.length === 4) {
                    const studentId = parts[3];
                    const idx = group.students.findIndex(s => String(s.id) === String(studentId));
                    if (idx >= 0) group.students.splice(idx, 1);
                    persist();
                    return null;
                }
            }
            if (parts[2] === 'preferences' && m === 'PUT') {
                const formatted = (Array.isArray(body) ? body : []).map(mapPreferenceToGroupApiFormat);
                group.preferences = formatted;
                persist();
                return jsonResponse(formatted);
            }
        }
    }

    // Students
    if (parts[0] === 'students') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.students || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const { email } = body || {};
            let existing = inMemoryStore.students.find(s => s.email === email);
            if (existing) return jsonResponse(existing);
            const item = { id: nextId('students'), wants_emails: true, ...body };
            inMemoryStore.students.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const studentId = parts[1];
            const student = getById(inMemoryStore.students, studentId);
            if (!student) throw new Error('Student not found');
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(student);
            }
            if (parts[2] === 'email-preference' && m === 'PATCH') {
                student.wants_emails = !!body?.wants_emails;
                persist();
                return jsonResponse({ ok: true });
            }
            if (parts[2] === 'unsubscribe' && m === 'POST') {
                student.wants_emails = false;
                persist();
                return jsonResponse({ ok: true });
            }
        }
    }

    // Rooms
    if (parts[0] === 'rooms') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.rooms || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const item = { id: nextId('rooms'), preferences: [], ...body };
            inMemoryStore.rooms.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const roomId = parts[1];
            const room = getById(inMemoryStore.rooms, roomId);
            if (!room) throw new Error('Room not found');
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(room);
            }
            if (m === 'PUT' && parts.length === 2) {
                Object.assign(room, body || {});
                persist();
                return jsonResponse(room);
            }
            if (m === 'DELETE' && parts.length === 2) {
                removeById(inMemoryStore.rooms, roomId);
                persist();
                return null;
            }
            if (parts[2] === 'preferences' && m === 'PUT') {
                room.preferences = Array.isArray(body) ? body : [];
                persist();
                return jsonResponse(room.preferences);
            }
        }
    }

    // Subjects
    if (parts[0] === 'subjects') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.subjects || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const item = { id: nextId('subjects'), ...body };
            inMemoryStore.subjects.push(item);
            persist();
            return jsonResponse(item);
        }
        if (m === 'DELETE' && parts.length === 2 && parts[1] === 'delete-all') {
            inMemoryStore.subjects = [];
            persist();
            return jsonResponse({ ok: true });
        }
        if (parts.length >= 2) {
            const id = parts[1];
            const subj = getById(inMemoryStore.subjects, id);
            if (!subj) throw new Error('Subject not found');
            if (m === 'GET' && parts.length === 2) return jsonResponse(subj);
            if (m === 'PUT' && parts.length === 2) { Object.assign(subj, body || {}); persist(); return jsonResponse(subj); }
            if (m === 'DELETE' && parts.length === 2) { removeById(inMemoryStore.subjects, id); persist(); return null; }
        }
    }

    // Schedule Entries
    if (parts[0] === 'schedule-entries') {
        if (m === 'GET' && parts.length === 1) {
            // Optionally filter by queryParams
            let list = inMemoryStore.schedule_entries || [];
            if (queryParams && Object.keys(queryParams).length) {
                list = list.filter(e => Object.entries(queryParams).every(([k, v]) => String(e[k]) === String(v)));
            }
            return jsonResponse(list);
        }
        if (m === 'POST' && parts.length === 1) {
            const item = { id: nextId('schedule_entries'), ...body };
            inMemoryStore.schedule_entries.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length === 2) {
            const id = parts[1];
            const entry = getById(inMemoryStore.schedule_entries, id);
            if (!entry) throw new Error('Schedule entry not found');
            if (m === 'PUT') { Object.assign(entry, body || {}); persist(); return jsonResponse(entry); }
            if (m === 'DELETE') { removeById(inMemoryStore.schedule_entries, id); persist(); return null; }
        }
        if (parts[1] === 'import' && m === 'POST') {
            const arr = Array.isArray(body) ? body : [];
            arr.forEach(e => { inMemoryStore.schedule_entries.push({ id: nextId('schedule_entries'), ...e }); });
            persist();
            return jsonResponse({ count: arr.length });
        }
        if (parts[1] === 'drafts') {
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(inMemoryStore.drafts || []);
            }
            if (m === 'POST' && parts.length === 3 && parts[2] === 'create_empty') {
                const { draft_name, year } = body || {};
                const draft = { id: nextId('drafts'), name: draft_name, year, entries: [] };
                inMemoryStore.drafts.push(draft);
                persist();
                return jsonResponse(draft);
            }
            if (parts.length >= 3) {
                const draftName = decodeURIComponent(parts[2]);
                const draft = (inMemoryStore.drafts || []).find(d => d.name === draftName);
                if (m === 'POST' && parts.length === 4 && parts[3] === 'save') {
                    const { year } = body || {};
                    const snapshot = (inMemoryStore.schedule_entries || []).filter(e => String(e.year) === String(year));
                    if (draft) { draft.entries = snapshot; draft.year = year; }
                    else inMemoryStore.drafts.push({ id: nextId('drafts'), name: draftName, year, entries: snapshot });
                    persist();
                    return jsonResponse({ ok: true });
                }
                if (m === 'POST' && parts.length === 4 && parts[3] === 'load') {
                    if (!draft) throw new Error('Draft not found');
                    // Replace current entries for that year
                    inMemoryStore.schedule_entries = (inMemoryStore.schedule_entries || []).filter(e => String(e.year) !== String(draft.year)).concat(draft.entries);
                    persist();
                    return jsonResponse({ ok: true });
                }
                if (m === 'DELETE' && parts.length === 3) {
                    if (draft) removeById(inMemoryStore.drafts, draft.id);
                    persist();
                    return null;
                }
                if (m === 'POST' && parts.length === 5 && parts[3] === 'publish-period') {
                    // No-op publish in mock
                    return jsonResponse({ ok: true });
                }
            }
        }
        if (parts[1] === 'debug' && parts[2] === 'delete-all' && m === 'DELETE') {
            inMemoryStore.schedule_entries = [];
            persist();
            return null;
        }
        if (parts[1] === 'notify' && m === 'POST') {
            // Return a trivial result indicating notifications would be sent
            return jsonResponse({ ok: true, messagesSent: (Array.isArray(body?.changes) ? body.changes.length : 0) });
        }
    }

    // Year Structure
    if (parts[0] === 'year-structure') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.yearStructure || null);
        }
        if (m === 'POST' && parts.length === 1) {
            // Preserve/generate IDs for periods and holidays
            const existing = inMemoryStore.yearStructure || {};
            const existingPeriods = existing.periods || [];
            const existingHolidays = existing.holidays || [];
            
            // Map periods: preserve IDs if they match by name/weeks, otherwise generate new ones
            const periods = (body.periods || []).map((period, idx) => {
                if (period.id) {
                    // If ID is provided, try to find existing period
                    const existingPeriod = existingPeriods.find(p => p.id === period.id);
                    if (existingPeriod) {
                        return { ...existingPeriod, ...period, id: existingPeriod.id };
                    }
                }
                // Generate new ID or use existing if found by matching criteria
                const matched = existingPeriods.find(p => 
                    p.name === period.name && 
                    p.startWeek === period.startWeek && 
                    p.endWeek === period.endWeek
                );
                return {
                    ...period,
                    id: matched ? matched.id : (existingPeriods.length > 0 ? Math.max(...existingPeriods.map(p => p.id || 0)) + idx + 1 : idx + 1)
                };
            });
            
            // Map holidays: preserve IDs if they match by name/dates, otherwise generate new ones
            const holidays = (body.holidays || []).map((holiday, idx) => {
                if (holiday.id) {
                    // If ID is provided, try to find existing holiday
                    const existingHoliday = existingHolidays.find(h => h.id === holiday.id);
                    if (existingHoliday) {
                        return { ...existingHoliday, ...holiday, id: existingHoliday.id };
                    }
                }
                // Generate new ID or use existing if found by matching criteria
                const matched = existingHolidays.find(h => 
                    h.name === holiday.name && 
                    h.startDate === holiday.startDate && 
                    h.endDate === holiday.endDate
                );
                return {
                    ...holiday,
                    id: matched ? matched.id : (existingHolidays.length > 0 ? Math.max(...existingHolidays.map(h => h.id || 0)) + idx + 1 : idx + 1)
                };
            });
            
            // Update year structure with preserved/generated IDs
            inMemoryStore.yearStructure = {
                ...body,
                periods,
                holidays
            };
            persist();
            
            // Return in the same format as real backend: { message, yearStructure }
            return jsonResponse({
                message: 'Year structure saved successfully',
                yearStructure: inMemoryStore.yearStructure
            });
        }
        if (m === 'GET' && parts.length === 2 && parts[1] === 'years') {
            // Minimal summary: one item if yearStructure exists
            const ys = inMemoryStore.yearStructure;
            const years = ys ? [{ id: ys.academicYearId || 1, name: ys.academicYear, startWeek: ys.startWeek, endWeek: ys.endWeek }] : [];
            return jsonResponse(years);
        }
        if (m === 'DELETE' && parts.length === 3 && parts[1] === 'years') {
            inMemoryStore.yearStructure = null;
            persist();
            return null;
        }
    }

    // Institution
    if (parts[0] === 'institution') {
        if (parts[1] === 'live-schedule-version' && m === 'GET' && parts.length === 2) {
            const settings = inMemoryStore.institutionSettings || {};
            const live_schedule_version = settings.live_schedule_version != null ? settings.live_schedule_version : 1;
            return jsonResponse({ live_schedule_version });
        }
        if (parts[1] === 'settings') {
            if (m === 'GET') return jsonResponse(inMemoryStore.institutionSettings || {});
            if (m === 'PUT') { inMemoryStore.institutionSettings = { ...(inMemoryStore.institutionSettings || {}), ...body }; persist(); return jsonResponse(inMemoryStore.institutionSettings); }
            // POST /institution/settings/icon — mock URL (no real file in NO_SERVER mode)
            if (m === 'POST' && parts[2] === 'icon') {
                return jsonResponse({ url: 'https://via.placeholder.com/128.png?text=School+icon' });
            }
        }
        if (parts[1] === 'admins') {
            if (m === 'GET' && parts.length === 2) return jsonResponse(inMemoryStore.admins || []);
            if (parts[2] === 'invitations') {
                // POST /institution/admins/invitations
                if (m === 'POST' && parts.length === 3) {
                    const inv = { id: nextId('invitations'), email: body?.email, token: `token_${Date.now()}`, status: 'pending' };
                    inMemoryStore.invitations.push(inv);
                    persist();
                    return jsonResponse(inv);
                }
                // POST /institution/admins/invitations/:invitationId/resend
                if (m === 'POST' && parts.length === 5 && parts[4] === 'resend') {
                    return jsonResponse({ ok: true });
                }
                // DELETE /institution/admins/invitations/:invitationId
                if (m === 'DELETE' && parts.length === 4) {
                    const id = parts[3];
                    removeById(inMemoryStore.invitations, id);
                    persist();
                    return null;
                }
            }
            // DELETE /institution/admins/:userId
            if (m === 'DELETE' && parts.length === 3) {
                const id = parts[2];
                removeById(inMemoryStore.admins, id);
                persist();
                return null;
            }
        }
    }

    // Setup onboarding
    if (parts[0] === 'setup' && parts[1] === 'institution' && m === 'POST') {
        const { institutionName, name } = body || {};
        inMemoryStore.institutionSettings = { ...(inMemoryStore.institutionSettings || {}), name: institutionName };
        inMemoryStore.admins = inMemoryStore.admins || [];
        const newAdmin = { id: nextId('admins'), email: `${name?.toLowerCase() || 'admin'}@example.com`, role: 'Admin' };
        inMemoryStore.admins.push(newAdmin);
        persist();
        return jsonResponse({ ok: true, admin: newAdmin });
    }

    // Invitations public endpoints
    if (parts[0] === 'invitations') {
        if (parts[1] === 'accept' && m === 'POST') {
            const inv = inMemoryStore.invitations.find(i => i.token === body?.token);
            if (!inv) throw new Error('Invalid token');
            inv.status = 'accepted';
            persist();
            return jsonResponse({ ok: true });
        }
        if (parts[1] === 'lookup' && m === 'GET') {
            const token = queryParams?.token;
            const inv = inMemoryStore.invitations.find(i => i.token === token);
            if (!inv) throw new Error('Invitation not found');
            return jsonResponse({ email: inv.email, status: inv.status });
        }
    }

    logger.warn('[mockApiService] Unhandled route:', method, endpointPath);
    return jsonResponse({});
};

export /**
 * Reset mock data cache to force reload from mock-data.json on next access.
 * This is useful when you've edited mock-data.json and want to see the changes.
 */
const resetMockData = () => {
    inMemoryStore = null;
    idTracker = null;
    try { 
        sessionStorage.removeItem(STORAGE_KEY); 
        sessionStorage.removeItem(ID_TRACKER_KEY); 
    } catch (e) {
        // ignore
    }
};

export const initializeApiService = () => {};

export default {
    mockFetch,
    resetMockData,
    initializeApiService
};


